import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    LinkedIn as LinkedInIcon,
    Description as DescriptionIcon,
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Folder as FolderIcon,
    Star as StarIcon,
    AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAppTheme } from '../../../App';
import { DocumentService } from '../services/documentService';
import { UserDocument } from '../types/document.types';
import { generateGTVPersonalStatement } from '../services/gtvService';
import AuthLayout from '../../../components/AuthLayout';
import DocumentManagerDialog from './DocumentManagerDialog';
import { useSubscription } from '../../../hooks/useSubscription';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { incrementPersonalStatementGenerated } from '../../../services/analyticsService';


const PersonalStatementGenerator: React.FC = () => {
    const { getThemeStyles, isDarkMode } = useAppTheme();
    const theme = getThemeStyles();
    const { loading: subscriptionLoading, purchase, isInitialized: revenueCatInitialized, isPro } = useSubscription();
    const additionalDocumentsLimit = isPro ? 10 : 3;
    const { 
        trackFeatureUsage, 
        trackCustomEvent, 
        trackError 
    } = useAnalytics();

    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [customPrompt, setCustomPrompt] = useState('');
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    
    const [uploadStatus, setUploadStatus] = useState({
        linkedin: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
        cv: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
        additional: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
    });

    const [uploadedDocs, setUploadedDocs] = useState<{
        linkedin?: UserDocument;
        cv?: UserDocument;
        additional: UserDocument[];
    }>({
        additional: [],
    });

    const [alertMessage, setAlertMessage] = useState<{
        type: 'success' | 'error' | 'info';
        message: string;
    } | null>(null);

    const [documentManagerOpen, setDocumentManagerOpen] = useState(false);

    // Load existing documents on component mount
    useEffect(() => {
        const loadExistingDocuments = async () => {
            setIsLoadingDocuments(true);
            try {
                const response = await DocumentService.getUserDocuments();
                if (response.status === 'success') {
                    const { documents } = response;
                    
                    // Organize documents by type
                    const linkedinDoc = documents.find(doc => doc.fileType === 'linkedin');
                    const cvDoc = documents.find(doc => doc.fileType === 'cv');
                    const additionalDocs = documents.filter(doc => doc.fileType === 'additional');

                    // Update state with existing documents
                    setUploadedDocs({
                        linkedin: linkedinDoc,
                        cv: cvDoc,
                        additional: additionalDocs,
                    });

                    // Update upload status for existing documents
                    setUploadStatus({
                        linkedin: linkedinDoc ? 'success' : 'idle',
                        cv: cvDoc ? 'success' : 'idle',
                        additional: additionalDocs.length > 0 ? 'success' : 'idle',
                    });

                    // Set LinkedIn URL if it exists
                    if (linkedinDoc?.linkUrl) {
                        setLinkedinUrl(linkedinDoc.linkUrl);
                    }
                }
            } catch (error) {
                console.error('Error loading existing documents:', error);
                setAlertMessage({
                    type: 'error',
                    message: 'Failed to load existing documents'
                });
            } finally {
                setIsLoadingDocuments(false);
            }
        };

        loadExistingDocuments();
    }, []);

    // LinkedIn URL handling
    const handleLinkedInSubmit = async () => {
        if (!linkedinUrl.trim()) {
            setAlertMessage({ type: 'error', message: 'Please enter your LinkedIn URL' });
            return;
        }

        setUploadStatus(prev => ({ ...prev, linkedin: 'uploading' }));
        
        try {
            const response = await DocumentService.uploadDocument({
                fileType: 'linkedin',
                linkUrl: linkedinUrl,
            });
            
            if (response.status === 'success' && response.document) {
                setUploadedDocs(prev => ({ ...prev, linkedin: response.document }));
                setUploadStatus(prev => ({ ...prev, linkedin: 'success' }));
                setAlertMessage({ type: 'success', message: response.message });
            } else {
                setUploadStatus(prev => ({ ...prev, linkedin: 'error' }));
                setAlertMessage({ type: 'error', message: response.message });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, linkedin: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to save LinkedIn profile' });
        }
    };

    // CV file handling
    const onCvDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setUploadStatus(prev => ({ ...prev, cv: 'uploading' }));

        try {
            const response = await DocumentService.uploadDocument({
                fileType: 'cv',
                file: file,
            });
            
            if (response.status === 'success' && response.document) {
                setUploadedDocs(prev => ({ ...prev, cv: response.document }));
                setUploadStatus(prev => ({ ...prev, cv: 'success' }));
                setAlertMessage({ type: 'success', message: response.message });
            } else {
                setUploadStatus(prev => ({ ...prev, cv: 'error' }));
                setAlertMessage({ type: 'error', message: response.message });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, cv: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to upload CV' });
        }
    }, []);

    // Additional documents handling
    const onAdditionalDrop = useCallback(async (acceptedFiles: File[]) => {
        const remainingSlots = additionalDocumentsLimit - uploadedDocs.additional.length;
        if (acceptedFiles.length > remainingSlots) {
            setAlertMessage({ 
                type: 'error', 
                message: `You can only upload ${remainingSlots} more additional document(s)` 
            });
            return;
        }

        setUploadStatus(prev => ({ ...prev, additional: 'uploading' }));

        try {
            const uploadPromises = acceptedFiles.map(file => 
                DocumentService.uploadDocument({
                    fileType: 'additional',
                    file: file,
                }, {
                    additionalDocumentsLimit
                })
            );

            const responses = await Promise.all(uploadPromises);
            const successfulUploads = responses
                .filter(response => response.status === 'success' && response.document)
                .map(response => response.document!);

            if (successfulUploads.length > 0) {
                setUploadedDocs(prev => ({
                    ...prev,
                    additional: [...prev.additional, ...successfulUploads],
                }));
                setUploadStatus(prev => ({ ...prev, additional: 'success' }));
                setAlertMessage({ 
                    type: 'success', 
                    message: `${successfulUploads.length} additional document(s) uploaded successfully` 
                });
            } else {
                setUploadStatus(prev => ({ ...prev, additional: 'error' }));
                setAlertMessage({ type: 'error', message: 'Failed to upload additional documents' });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, additional: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to upload additional documents' });
        }
    }, [uploadedDocs.additional.length]);

    const handleDeleteAdditionalDoc = async (docId: string) => {
        try {
            await DocumentService.deleteDocument(docId);
            setUploadedDocs(prev => ({
                ...prev,
                additional: prev.additional.filter(doc => doc.id !== docId),
            }));
            setAlertMessage({ type: 'success', message: 'Document deleted successfully' });
        } catch (error) {
            setAlertMessage({ type: 'error', message: 'Failed to delete document' });
        }
    };

    const { getRootProps: getCvRootProps, getInputProps: getCvInputProps, isDragActive: isCvDragActive } = useDropzone({
        onDrop: onCvDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        },
        maxFiles: 1,
        disabled: uploadStatus.cv === 'uploading' || uploadedDocs.cv !== undefined,
    });

    const { getRootProps: getAdditionalRootProps, getInputProps: getAdditionalInputProps, isDragActive: isAdditionalDragActive } = useDropzone({
        onDrop: onAdditionalDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
        },
        maxFiles: additionalDocumentsLimit - uploadedDocs.additional.length,
        disabled: uploadStatus.additional === 'uploading' || uploadedDocs.additional.length >= additionalDocumentsLimit,
    });

    const getStatusIcon = (status: 'idle' | 'uploading' | 'success' | 'error') => {
        switch (status) {
            case 'uploading':
                return <CircularProgress size={20} />;
            case 'success':
                return <CheckCircleIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            default:
                return null;
        }
    };

    const canGenerateStatement = uploadedDocs.linkedin && uploadedDocs.cv;

    // Check subscription and generate statement
    const handleGenerateButtonClick = () => {
        // Track button click
        trackCustomEvent('personal_statement_generate_button_clicked', {
            visa_type: 'global_talent_visa',
            has_linkedin: !!uploadedDocs.linkedin,
            has_cv: !!uploadedDocs.cv,
            has_additional_docs: uploadedDocs.additional.length > 0,
            additional_docs_count: uploadedDocs.additional.length,
            has_custom_prompt: customPrompt.trim().length > 0,
        });

        if (!canGenerateStatement) {
            setAlertMessage({ 
                type: 'error', 
                message: 'Please upload your LinkedIn profile and CV first' 
            });
            return;
        }

        // Always show payment dialog for pay-per-use model
        setShowPaymentDialog(true);
    };

    // Generate personal statement
    const handleGenerateStatement = async () => {
        setIsGenerating(true);
        setAlertMessage(null);

        // Increment analytics count
        try {
            await incrementPersonalStatementGenerated();
        } catch (error) {
            console.error('Error incrementing analytics count:', error);
            // Continue with generation even if analytics increment fails
        }

        // Track generation attempt
        trackFeatureUsage('personal_statement_generation', 'global_talent_visa');

        try {
            const response = await generateGTVPersonalStatement({
                custom_prompt: customPrompt.trim() || undefined
            });

            // Create download link and trigger download
            const url = window.URL.createObjectURL(response.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Track successful generation
            trackCustomEvent('personal_statement_generated_success', {
                visa_type: 'global_talent_visa',
                has_custom_prompt: customPrompt.trim().length > 0,
                filename: response.filename,
            });

            setAlertMessage({
                type: 'success',
                message: 'Personal statement template generated and downloaded successfully!'
            });
        } catch (error) {
            console.error('Error generating personal statement:', error);
            
            // Track generation error
            trackError(error instanceof Error ? error : new Error('Generation failed'), 'personal_statement_generation');

            setAlertMessage({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to generate personal statement template'
            });
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle pay-per-generation purchase
    const handlePurchaseGeneration = async () => {
        setIsPurchasing(true);
        setAlertMessage(null);

        // Track purchase attempt
        trackCustomEvent('personal_statement_purchase_initiated', {
            visa_type: 'global_talent_visa',
            product_id: '1_time_personal_statement',
        });

        try {
            // Use the "standart" offering as mentioned by the user
            await purchase('1_time_personal_statement');
            
            // Track successful purchase
            trackCustomEvent('personal_statement_purchase_success', {
                visa_type: 'global_talent_visa',
                product_id: '1_time_personal_statement',
            });
            
            setShowPaymentDialog(false);
            setAlertMessage({
                type: 'success',
                message: 'Payment successful! Generating your personal statement...'
            });
            // Proceed with generation after successful payment
            setTimeout(() => {
                handleGenerateStatement();
            }, 1000);
        } catch (error) {
            console.error('Purchase failed:', error);
            
            // Track purchase failure
            trackError(error instanceof Error ? error : new Error('Purchase failed'), 'personal_statement_purchase');
            
            setAlertMessage({
                type: 'error',
                message: error instanceof Error ? error.message : 'Payment failed. Please try again.'
            });
        } finally {
            setIsPurchasing(false);
        }
    };

    return (
        <AuthLayout>
            <Box sx={{ 
                background: theme.background, 
                minHeight: 'calc(100vh - 64px)', // Subtract top nav bar height
                pt: { xs: 1, sm: 2 },
                pb: { xs: 1, sm: 2 }
            }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center', position: 'relative' }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontWeight: 700, 
                        background: theme.gradientText,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}
                >
                    Personal Statement Generator
                </Typography>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: theme.textSecondary, 
                        maxWidth: 600, 
                        mx: 'auto',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                >
                    Upload your LinkedIn profile, CV, and supporting documents to generate a personalized statement.
                </Typography>
                
                {/* Manage Documents Button */}
                <Box sx={{ 
                    position: { xs: 'static', sm: 'absolute' }, 
                    top: 0, 
                    right: 0,
                    mt: { xs: 2, sm: 0 },
                    textAlign: { xs: 'center', sm: 'left' }
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<FolderIcon />}
                        onClick={() => setDocumentManagerOpen(true)}
                        sx={{
                            borderRadius: 2,
                            borderColor: theme.borderColor,
                            color: theme.textPrimary,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.75, sm: 1 },
                            '&:hover': {
                                borderColor: theme.primary,
                                backgroundColor: 'rgba(254, 106, 46, 0.08)',
                                color: theme.primary,
                            }
                        }}
                    >
                        Manage Documents
                    </Button>
                </Box>
            </Box>

            {/* Alert Messages */}
            {alertMessage && (
                <Alert 
                    severity={alertMessage.type} 
                    sx={{ mb: 3 }}
                    onClose={() => setAlertMessage(null)}
                >
                    {alertMessage.message}
                </Alert>
            )}

            {/* Loading State */}
            {isLoadingDocuments && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    py: 4,
                    mb: 3
                }}>
                    <CircularProgress sx={{ mr: 2 }} />
                    <Typography variant="body1" sx={{ color: theme.textSecondary }}>
                        Loading your documents...
                    </Typography>
                </Box>
            )}

            {/* LinkedIn Profile Section */}
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Card sx={{ 
                    background: theme.cardBackground, 
                    border: `1px solid ${theme.borderColor}`,
                    borderRadius: 3,
                    boxShadow: `0 2px 8px ${theme.shadowColor}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: `0 4px 16px ${theme.shadowColor}`,
                    }
                }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: 2,
                                background: 'rgba(0, 119, 181, 0.1)',
                                mr: 2
                            }}>
                                <LinkedInIcon sx={{ color: '#0077B5', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Typography variant="h6" sx={{ 
                                color: theme.textPrimary, 
                                fontWeight: 600, 
                                fontSize: { xs: '1rem', sm: '1.2rem' }
                            }}>
                                LinkedIn Profile
                            </Typography>
                            <Chip 
                                label="Required" 
                                size="small" 
                                color="primary" 
                                sx={{ 
                                    ml: { xs: 0, sm: 2 }, 
                                    mt: { xs: 1, sm: 0 },
                                    height: 24, 
                                    fontWeight: 500,
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }} 
                            />
                            <Box sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                                {getStatusIcon(uploadStatus.linkedin)}
                            </Box>
                        </Box>
                        
                        {!uploadedDocs.linkedin ? (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: { xs: 1, sm: 2 }, 
                                alignItems: 'flex-end',
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <TextField
                                    fullWidth
                                    label="LinkedIn Profile URL"
                                    placeholder="https://www.linkedin.com/in/your-profile"
                                    value={linkedinUrl}
                                    onChange={(e) => setLinkedinUrl(e.target.value)}
                                    disabled={uploadStatus.linkedin === 'uploading'}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            background: theme.inputBackground,
                                            borderRadius: 2,
                                            color: theme.textPrimary,
                                            '& input': {
                                                color: theme.textPrimary,
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            },
                                            '& input::placeholder': {
                                                color: theme.textSecondary,
                                                opacity: 0.7,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.primary,
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.borderColor,
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.textSecondary,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            '&.Mui-focused': {
                                                color: theme.primary,
                                            }
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleLinkedInSubmit}
                                    disabled={uploadStatus.linkedin === 'uploading' || !linkedinUrl.trim()}
                                    startIcon={uploadStatus.linkedin === 'uploading' ? <CircularProgress size={16} /> : <LinkedInIcon />}
                                    sx={{ 
                                        whiteSpace: 'nowrap', 
                                        minHeight: { xs: 48, sm: 56 },
                                        px: { xs: 2, sm: 3 },
                                        borderRadius: 2,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        background: `linear-gradient(135deg, ${theme.primary} 0%, #FF8F5A 100%)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, #FF8F5A 0%, #E5511A 100%)`,
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px rgba(254, 106, 46, 0.3)`,
                                        }
                                    }}
                                >
                                    {uploadStatus.linkedin === 'uploading' ? 'Saving...' : 'Save Profile'}
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)`,
                                border: `1px solid rgba(76, 175, 80, 0.2)`,
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 1, sm: 0 }
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ color: '#4CAF50', mr: 1 }} />
                                    <Box>
                                        <Typography sx={{ 
                                            color: theme.textPrimary, 
                                            fontWeight: 500,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            LinkedIn Profile Successfully Saved
                                        </Typography>
                                        {uploadedDocs.linkedin?.linkUrl && (
                                            <Typography variant="caption" sx={{ 
                                                color: theme.textSecondary, 
                                                display: 'block', 
                                                mt: 0.5,
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                            }}>
                                                {uploadedDocs.linkedin.linkUrl}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={async () => {
                                            if (uploadedDocs.linkedin) {
                                                try {
                                                    await DocumentService.deleteDocument(uploadedDocs.linkedin.id);
                                                    setUploadedDocs(prev => ({ ...prev, linkedin: undefined }));
                                                    setUploadStatus(prev => ({ ...prev, linkedin: 'idle' }));
                                                    setLinkedinUrl('');
                                                    setAlertMessage({ type: 'success', message: 'LinkedIn profile removed. You can now add a new one.' });
                                                } catch (error) {
                                                    setAlertMessage({ type: 'error', message: 'Failed to remove LinkedIn profile' });
                                                }
                                            }
                                        }}
                                        sx={{
                                            borderColor: theme.borderColor,
                                            color: theme.textSecondary,
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            px: { xs: 1, sm: 1.5 },
                                            py: { xs: 0.25, sm: 0.5 },
                                            '&:hover': {
                                                borderColor: theme.primary,
                                                color: theme.primary,
                                            }
                                        }}
                                    >
                                        Replace
                                    </Button>
                                    <Chip 
                                        label="✓ Complete" 
                                        size="small" 
                                        sx={{ 
                                            background: '#4CAF50', 
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                        }} 
                                    />
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* CV and Additional Documents - Two Column Layout */}
            <Box sx={{ 
                display: 'flex', 
                gap: { xs: 2, sm: 3 }, 
                mb: { xs: 2, sm: 3 }, 
                alignItems: 'stretch',
                flexDirection: { xs: 'column', sm: 'row' }
            }}>
                {/* CV Upload Section */}
                <Box sx={{ flex: 1 }}>
                    <Card sx={{ 
                        background: theme.cardBackground, 
                        border: `1px solid ${theme.borderColor}`, 
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: `0 2px 8px ${theme.shadowColor}`,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            boxShadow: `0 4px 16px ${theme.shadowColor}`,
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: 2,
                                    background: `rgba(254, 106, 46, 0.1)`,
                                    mr: 2
                                }}>
                                    <DescriptionIcon sx={{ color: theme.primary, fontSize: { xs: 20, sm: 24 } }} />
                                </Box>
                                <Typography variant="h6" sx={{ 
                                    color: theme.textPrimary, 
                                    fontWeight: 600, 
                                    fontSize: { xs: '1rem', sm: '1.2rem' }
                                }}>
                                    CV/Resume
                                </Typography>
                                <Chip 
                                    label="Required" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ 
                                        ml: { xs: 0, sm: 2 }, 
                                        mt: { xs: 1, sm: 0 },
                                        height: 24, 
                                        fontWeight: 500,
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }} 
                                />
                                <Box sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                                    {getStatusIcon(uploadStatus.cv)}
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{ 
                                color: theme.textSecondary, 
                                mb: 2, 
                                opacity: 0.8,
                                fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }}>
                                Upload your curriculum vitae or resume document for your visa application.
                            </Typography>

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {!uploadedDocs.cv ? (
                                    <Box
                                        {...getCvRootProps()}
                                        sx={{
                                            border: `2px dashed ${isCvDragActive ? theme.primary : theme.borderColor}`,
                                            borderRadius: 3,
                                            p: { xs: 2, sm: 4 },
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            background: isCvDragActive 
                                                ? `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)` 
                                                : `linear-gradient(135deg, ${theme.cardBackground} 0%, rgba(255,255,255,0.02) 100%)`,
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            borderColor: theme.primary,
                                            background: `linear-gradient(135deg, ${theme.primary}10 0%, ${theme.primary}05 100%)`,
                                            transform: 'translateY(-2px)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `radial-gradient(circle at center, ${theme.primary}05 0%, transparent 70%)`,
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                        },
                                        '&:hover::before': {
                                            opacity: 1,
                                        }
                                    }}
                                >
                                    <input {...getCvInputProps()} />
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        width: { xs: 48, sm: 64 },
                                        height: { xs: 48, sm: 64 },
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${theme.primary}20 0%, ${theme.primary}10 100%)`,
                                        mx: 'auto',
                                        mb: 2
                                    }}>
                                        <CloudUploadIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.primary }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.textPrimary, 
                                        mb: 1, 
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        {isCvDragActive ? 'Drop your CV here' : 'Upload your CV'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.textSecondary, 
                                        mb: 1,
                                        fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                    }}>
                                        Drag and drop or click to browse
                                    </Typography>
                                    <Typography variant="caption" sx={{ 
                                        color: theme.textSecondary, 
                                        opacity: 0.7,
                                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                    }}>
                                        PDF, DOC, DOCX (Max 10MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    border: `1px solid rgba(76, 175, 80, 0.2)`,
                                    borderRadius: 3,
                                    p: { xs: 2, sm: 3 },
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)`,
                                    minHeight: { xs: 60, sm: 80 },
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 0 }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            width: { xs: 32, sm: 40 },
                                            height: { xs: 32, sm: 40 },
                                            borderRadius: 2,
                                            background: 'rgba(76, 175, 80, 0.1)',
                                            mr: 2
                                        }}>
                                            <DescriptionIcon sx={{ color: '#4CAF50', fontSize: { xs: 16, sm: 20 } }} />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ 
                                                color: theme.textPrimary, 
                                                fontWeight: 500, 
                                                mb: 0.5,
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}>
                                                {uploadedDocs.cv.fileName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ 
                                                color: theme.textSecondary,
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                            }}>
                                                CV successfully uploaded
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Chip 
                                        label="✓ Complete" 
                                        size="small" 
                                        sx={{ 
                                            background: '#4CAF50', 
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                        }} 
                                    />
                                </Box>
                            )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Additional Documents Section */}
                <Box sx={{ flex: 1 }}>
                    <Card sx={{ 
                        background: theme.cardBackground, 
                        border: `1px solid ${theme.borderColor}`, 
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: `0 2px 8px ${theme.shadowColor}`,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            boxShadow: `0 4px 16px ${theme.shadowColor}`,
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: 2,
                                    background: `rgba(254, 106, 46, 0.1)`,
                                    mr: 2
                                }}>
                                    <AttachFileIcon sx={{ color: theme.primary, fontSize: { xs: 20, sm: 24 } }} />
                                </Box>
                                <Typography variant="h6" sx={{ 
                                    color: theme.textPrimary, 
                                    fontWeight: 600, 
                                    fontSize: { xs: '1rem', sm: '1.2rem' }
                                }}>
                                    Additional Documents
                                </Typography>
                                <Chip 
                                    label={`${uploadedDocs.additional.length}/${additionalDocumentsLimit}`} 
                                    size="small" 
                                    color={uploadedDocs.additional.length === 0 ? "default" : "primary"}
                                    sx={{ 
                                        ml: { xs: 0, sm: 2 }, 
                                        mt: { xs: 1, sm: 0 },
                                        height: 24, 
                                        fontWeight: 500,
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }} 
                                />
                                <Box sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                                    {getStatusIcon(uploadStatus.additional)}
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{ 
                                color: theme.textSecondary, 
                                mb: 2, 
                                opacity: 0.8,
                                fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }}>
                                Upload supporting documents like certificates, awards, reference letters, etc.
                            </Typography>

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Uploaded Documents List */}
                            {uploadedDocs.additional.length > 0 && (
                                <Box sx={{ mb: 1.5 }}>
                                    <List dense sx={{ py: 0 }}>
                                        {uploadedDocs.additional.map((doc) => (
                                            <ListItem key={doc.id} sx={{ px: 0, py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 30 }}>
                                                    <AttachFileIcon sx={{ color: theme.textSecondary, fontSize: 16 }} />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={doc.fileName}
                                                    primaryTypographyProps={{
                                                        sx: { fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: theme.textPrimary }
                                                    }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Delete document">
                                                        <IconButton 
                                                            edge="end" 
                                                            size="small"
                                                            onClick={() => handleDeleteAdditionalDoc(doc.id)}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                                {/* Upload Area */}
                                {uploadedDocs.additional.length < additionalDocumentsLimit && (
                                    <Box
                                        {...getAdditionalRootProps()}
                                        sx={{
                                            border: `2px dashed ${isAdditionalDragActive ? theme.primary : theme.borderColor}`,
                                            borderRadius: 3,
                                            p: uploadedDocs.additional.length > 0 ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 4 },
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            background: isAdditionalDragActive 
                                                ? `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)` 
                                                : `linear-gradient(135deg, ${theme.cardBackground} 0%, rgba(255,255,255,0.02) 100%)`,
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            borderColor: theme.primary,
                                            background: `linear-gradient(135deg, ${theme.primary}10 0%, ${theme.primary}05 100%)`,
                                            transform: 'translateY(-2px)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `radial-gradient(circle at center, ${theme.primary}05 0%, transparent 70%)`,
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                        },
                                        '&:hover::before': {
                                            opacity: 1,
                                        }
                                    }}
                                >
                                    <input {...getAdditionalInputProps()} />
                                    {uploadedDocs.additional.length === 0 && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            width: { xs: 48, sm: 64 },
                                            height: { xs: 48, sm: 64 },
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${theme.primary}20 0%, ${theme.primary}10 100%)`,
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <CloudUploadIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.primary }} />
                                        </Box>
                                    )}
                                    {uploadedDocs.additional.length > 0 && (
                                        <AttachFileIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: theme.primary, mb: 1 }} />
                                    )}
                                    <Typography variant={uploadedDocs.additional.length > 0 ? "body2" : "h6"} sx={{ 
                                        color: theme.textPrimary, 
                                        mb: 0.5, 
                                        fontWeight: 600,
                                        fontSize: uploadedDocs.additional.length > 0 
                                            ? { xs: '0.85rem', sm: '0.9rem' }
                                            : { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        {isAdditionalDragActive ? 'Drop files here' : uploadedDocs.additional.length > 0 ? 'Add more documents' : 'Upload documents'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ 
                                        color: theme.textSecondary, 
                                        opacity: 0.7,
                                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                    }}>
                                                                                          PDF, DOC, DOCX, JPG, PNG<br/>(Max 10MB each, {additionalDocumentsLimit - uploadedDocs.additional.length} remaining)
                                    </Typography>
                                </Box>
                            )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Custom Prompt Section */}
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Card sx={{ 
                    background: theme.cardBackground, 
                    border: `1px solid ${theme.borderColor}`,
                    borderRadius: 3,
                    boxShadow: `0 2px 8px ${theme.shadowColor}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: `0 4px 16px ${theme.shadowColor}`,
                    }
                }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                borderRadius: 2,
                                background: `rgba(156, 39, 176, 0.1)`,
                                mr: 1.5
                            }}>
                                <InfoIcon sx={{ color: '#9C27B0', fontSize: { xs: 16, sm: 20 } }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ 
                                color: theme.textPrimary, 
                                fontWeight: 600, 
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}>
                                Custom Instructions
                            </Typography>
                            <Chip 
                                label="Optional" 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                    ml: { xs: 0, sm: 1.5 }, 
                                    mt: { xs: 1, sm: 0 },
                                    height: 20, 
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    fontWeight: 500,
                                    borderColor: theme.borderColor,
                                    color: theme.textSecondary
                                }} 
                            />
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Custom Instructions (Optional)"
                            placeholder="e.g., Emphasize AI/ML experience, leadership roles, professional tone..."
                            value={customPrompt}
                            onChange={(e) => setCustomPrompt(e.target.value)}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.inputBackground,
                                    borderRadius: 2,
                                    color: theme.textPrimary,
                                    '& textarea': {
                                        color: theme.textPrimary,
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    },
                                    '& textarea::placeholder': {
                                        color: theme.textSecondary,
                                        opacity: 0.7,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.primary,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.borderColor,
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme.textSecondary,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    '&.Mui-focused': {
                                        color: theme.primary,
                                    }
                                }
                            }}
                        />

                        {customPrompt.trim() && (
                            <Box sx={{ 
                                mt: 1.5,
                                p: { xs: 1, sm: 1.5 },
                                borderRadius: 2,
                                background: `linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%)`,
                                border: `1px solid rgba(156, 39, 176, 0.2)`
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ color: '#9C27B0', mr: 1, fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ 
                                        color: theme.textPrimary, 
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                    }}>
                                        Custom instructions will be used to personalize your statement
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* Generate Button Section */}
            <Box sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2 } }}>
                <Button
                    variant="contained"
                    size="large"
                    disabled={!canGenerateStatement || isGenerating || subscriptionLoading || !revenueCatInitialized}
                    onClick={handleGenerateButtonClick}
                    startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                        px: { xs: 4, sm: 6 },
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '1rem', sm: '1.2rem' },
                        fontWeight: 600,
                        borderRadius: 3,
                        background: (canGenerateStatement && !isGenerating)
                            ? `linear-gradient(135deg, ${theme.primary} 0%, #FF8F5A 100%)`
                            : theme.textSecondary,
                        color: 'white',
                        textTransform: 'none',
                        boxShadow: (canGenerateStatement && !isGenerating)
                            ? `0 4px 16px rgba(254, 106, 46, 0.3)`
                            : 'none',
                        transition: 'all 0.3s ease',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': (canGenerateStatement && !isGenerating) ? {
                            background: `linear-gradient(135deg, #FF8F5A 0%, #E5511A 100%)`,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 20px rgba(254, 106, 46, 0.4)`,
                        } : {},
                        '&:disabled': {
                            background: theme.textSecondary,
                            color: 'white',
                            cursor: 'not-allowed',
                        }
                    }}
                >
                    {isGenerating ? 'Generating...' : 'Generate Statement'}
                </Button>
                
                {!canGenerateStatement && !isGenerating && (
                    <Box sx={{ 
                        mt: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        background: `linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)`,
                        border: `1px solid rgba(255, 193, 7, 0.2)`
                    }}>
                        <InfoIcon sx={{ color: '#FFC107', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                        <Typography variant="body2" sx={{ 
                            color: theme.textPrimary, 
                            fontWeight: 500,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}>
                            Please upload your LinkedIn profile and CV to continue
                        </Typography>
                    </Box>
                )}

                {isGenerating && (
                    <Box sx={{ 
                        mt: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        background: `linear-gradient(135deg, rgba(254, 106, 46, 0.1) 0%, rgba(254, 106, 46, 0.05) 100%)`,
                        border: `1px solid rgba(254, 106, 46, 0.2)`
                    }}>
                        <CircularProgress size={16} sx={{ mr: 1, color: theme.primary }} />
                        <Typography variant="body2" sx={{ 
                            color: theme.textPrimary, 
                            fontWeight: 500,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}>
                            Generating your personalized statement template...
                        </Typography>
                    </Box>
                )}
            </Box>
                </Box>
            </Box>

            {/* Document Manager Dialog */}
            <DocumentManagerDialog 
                open={documentManagerOpen}
                onClose={() => setDocumentManagerOpen(false)}
            />

            {/* Payment Dialog */}
            <Dialog
                open={showPaymentDialog}
                onClose={() => setShowPaymentDialog(false)}
                maxWidth="sm"
                fullWidth
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)', // Full opacity backdrop
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: isDarkMode ? '#2a2a2a' : '#ffffff', // Full opacity background
                        border: `1px solid ${theme.borderColor}`,
                        mt: { xs: 2, sm: 6 }, // Add more top margin/padding to the dialog
                        mx: { xs: 1, sm: 2 }, // Add horizontal margin on mobile
                        opacity: 1, // Ensure full opacity
                    }
                }}
            >
                <DialogTitle sx={{ 
                    textAlign: 'center', 
                    pt: { xs: 3, sm: 5 },
                    pb: 2,
                    background: isDarkMode ? '#333333' : '#f8f9fa', // Full opacity header background
                    borderBottom: `1px solid ${theme.borderColor}`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${theme.primary} 0%, #FF8F5A 100%)`,
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 1, sm: 0 }
                        }}>
                            <StarIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                        </Box>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: theme.textPrimary,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                            Generate Personal Statement
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                        color: theme.textSecondary, 
                        mt: 1,
                        fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}>
                        One-time payment for this generation
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ 
                            color: theme.textPrimary, 
                            fontWeight: 600, 
                            mb: 2,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            Service Details
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 2, 
                            textAlign: 'left',
                            background: isDarkMode ? '#1a4a1f' : '#e8f5e8', // Full opacity green background
                            p: { xs: 2, sm: 3 },
                            borderRadius: 2,
                            border: `1px solid #4CAF50`
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ color: '#4CAF50', mr: 2 }} />
                                <Typography sx={{ 
                                    color: theme.textPrimary,
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }}>
                                    AI-generated personal statement template
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ color: '#4CAF50', mr: 2 }} />
                                <Typography sx={{ 
                                    color: theme.textPrimary,
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }}>
                                    Based on your LinkedIn & CV data
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ color: '#4CAF50', mr: 2 }} />
                                <Typography sx={{ 
                                    color: theme.textPrimary,
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }}>
                                    Custom instructions included
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ color: '#4CAF50', mr: 2 }} />
                                <Typography sx={{ 
                                    color: theme.textPrimary,
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }}>
                                    Instant DOCX download
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: theme.borderColor }} />
                    
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ 
                            color: theme.textSecondary, 
                            mb: 1,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}>
                            Pay once to generate this personal statement
                        </Typography>
                        <Typography variant="caption" sx={{ 
                            color: theme.textSecondary, 
                            opacity: 1,
                            fontSize: { xs: '0.75rem', sm: '0.8rem' }
                        }}>
                            One-time payment • Secure payment processing
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    pt: 0, 
                    flexDirection: 'column', 
                    gap: 2 
                }}>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={isPurchasing}
                        onClick={handlePurchaseGeneration}
                        startIcon={isPurchasing ? <CircularProgress size={20} color="inherit" /> : <StarIcon />}
                        sx={{
                            py: { xs: 1.25, sm: 1.5 },
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 600,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${theme.primary} 0%, #FF8F5A 100%)`,
                            color: 'white',
                            textTransform: 'none',
                            boxShadow: `0 4px 16px rgba(254, 106, 46, 0.3)`,
                            '&:hover': {
                                background: `linear-gradient(135deg, #FF8F5A 0%, #E5511A 100%)`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px rgba(254, 106, 46, 0.4)`,
                            }
                        }}
                    >
                        {isPurchasing ? 'Processing...' : 'Generate Now'}
                    </Button>
                    
                    <Button
                        variant="text"
                        size="large"
                        fullWidth
                        onClick={() => setShowPaymentDialog(false)}
                        disabled={isPurchasing}
                        sx={{
                            color: theme.textSecondary,
                            textTransform: 'none',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            '&:hover': {
                                backgroundColor: `${theme.textSecondary}10`,
                            }
                        }}
                    >
                        Maybe Later
                    </Button>
                </DialogActions>
            </Dialog>
        </AuthLayout>
    );
};

export default PersonalStatementGenerator; 